import {
  InvalidateOptions,
  InvalidateQueryFilters,
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";

interface InvalidateData extends InvalidateQueryFilters {
  options?: InvalidateOptions;
}

const invalidateQueries = async (
  queryClient: QueryClient,
  data: InvalidateData[],
) => {
  const dataWithOptions = data.map((d: InvalidateData) => ({
    type: "all" as const,
    ...d,
  }));
  return await Promise.all(
    dataWithOptions.map(async (data) => {
      const options = data.options || {
        cancelRefetch: true,
      };
      await queryClient.invalidateQueries(data, options);
    }),
  );
};

/**
 * Custom hook to invalidate queries using the query client.
 *
 * Example:
 * const invalidateQueries = useInvalidateQueries();
 * await invalidateQueries.run([
 *         {
 *           queryKey: usersQueryKey.me.queryKey
 *         }
 *       ])
 *
 * Because we use @lukemorales/query-key-factory to create query keys, we can use the query key directly.
 */
export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();
  return {
    run: (data: InvalidateData[]) => invalidateQueries(queryClient, data),
  };
};
